<template>
	<li
		class="position-relative main-nav__menu-item-no-hover position-relative"
		data-dd-action-name="user dropdown"
		data-test="user-dropdown-toggle"
		@click.stop="toggleDropdown"
	>
		<BaseLinkButton click-text="user dropdown">
			<span class="display-flex flex-direction-row align-items-center">
				<Avatar class="text-transform-none" avatar-size="nav" />
				<BaseSvgIcon
					name="chevron"
					width="22"
					height="22"
					:dir="dropdownExpanded ? 'up' : 'down'"
					class="ml-25 main-nav__simple-item-link dropdown-accordion-icon position-relative"
				/>
			</span>
		</BaseLinkButton>

		<ul
			v-click-away="onClickAway"
			class="main-nav__sub-menu main-nav__sub-menu--user-rollup mx-100"
			:class="{ 'main-nav__sub-menu--state-visible': dropdownExpanded }"
			@click.stop
		>
			<li
				class="display-flex flex-direction-column align-items-center pt-150 px-100"
				:class="{ 'bg-color-gold-20': isProUser }"
			>
				<Avatar avatar-size="medium" class="mb-50" />
				<div class="heading-5 text-align-center mb-100">{{ userName }}</div>
				<div v-if="isProUser" class="mb-150 display-flex justify-content-center align-items-center">
					<BaseLink
						v-if="!appStore.isAdvisor"
						:link="{ router: 'profile-pro-features', text: 'View Pro features' }"
						data-dd-action-name="pro cta link"
						data-test="join-pro-cta-link"
						class="button button-outline body-xs"
					/>
				</div>
			</li>
			<li class="account-section">
				<ul>
					<li class="main-nav__sub-menu-item" :class="!isProUser ? 'bt' : ''">
						<BaseLink
							:link="{ router: profileAndPreferencesLink }"
							classes="main-nav__sub-menu-item-link  text-align-left display-flex align-items-center"
							data-dd-action-name="menu profile and preferences"
						>
							<BaseSvgIcon name="user-outline" width="18" height="18" class="mr-100 dark" />
							<span class="dark">Profile &amp; Preferences</span>
						</BaseLink>
					</li>
					<li v-if="isEligibleForReferral" class="main-nav__sub-menu-item bt">
						<BaseLink
							:link="{ router: 'referral-landing' }"
							classes="main-nav__sub-menu-item-link  text-align-left display-flex align-items-center"
							data-dd-action-name="menu referral"
							data-test="invite-link"
						>
							<BaseSvgIcon name="gift" width="18" height="18" class="mr-100 text-color-rust" />
							<span class="dark">{{ referralLinkText }}</span>
						</BaseLink>
					</li>
					<li v-if="hasMultipleEntities" class="pb-50 bt">
						<div class="mx-100">
							<p class="font-weight-normal text-align-left mt-100"> Accounts </p>
							<NavInvestmentEntityList />
						</div>
					</li>

					<li v-if="!appStore.isAdvisor" class="main-nav__sub-menu-item bt">
						<BaseLink
							:link="{ router: 'settings-account-information-redirect' }"
							classes="main-nav__sub-menu-item-link  text-align-left display-flex align-items-center"
							data-dd-action-name="menu settings"
						>
							<BaseSvgIcon name="gear-outline" width="20" height="20" class="mr-100" />
							<span class="dark">Settings</span>
						</BaseLink>
					</li>

					<li class="main-nav__sub-menu-item bt">
						<BaseLink
							classes="main-nav__sub-menu-item-link  text-align-left display-flex align-items-center"
							data-dd-action-name="menu log out"
							mixpanel-target="Log out"
							:link="{ router: 'logout-success' }"
						>
							<BaseSvgIcon name="logout" width="20" height="20" class="mr-100" />
							<span class="dark">Log out</span>
						</BaseLink>
					</li>
				</ul>
			</li>
		</ul>
	</li>
</template>

<script setup lang="ts">
import Avatar from '@components/avatar/avatar-component.vue';
import { computed } from 'vue';
import { currency } from '@filters/shared-filters';
import NavInvestmentEntityList from '@components/header/navigation/nav-investment-entity-list.vue';
import { useAccountSetupChecklistStore } from '@stores/account-setup-checklist';
import { useAppStore } from '@stores/app';
import { useInvestmentEntityStore } from '@stores/investment-entity';
import { useUserStore } from '@stores/user';

interface Props {
	hasMultipleEntities?: boolean;
	expanded?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
	hasMultipleEntities: false,
	expanded: false
});

interface Emits {
	(e: 'expandUserDropdown', value: boolean): void;
}
const emit = defineEmits<Emits>();

const appStore = useAppStore();
const investmentEntityStore = useInvestmentEntityStore();
const userStore = useUserStore();
const accountSetupChecklistStore = useAccountSetupChecklistStore();

const isProUser = computed((): boolean => {
	return userStore.isSubscriptionActive;
});

const referralAmount = computed((): string => {
	return userStore.invitationDetails?.invitationDetails.rewardPerInvite ?? '';
});

const userName = computed((): string => {
	return userStore.user?.firstName && userStore.user?.lastName
		? `${userStore.user.firstName} ${userStore.user.lastName}`
		: 'Your Account';
});

const dropdownExpanded = computed((): boolean => {
	return props.expanded;
});

const isEligibleForReferral = computed((): boolean => {
	return investmentEntityStore.isEligibleForReferral;
});

const referralLinkText = computed((): string => {
	const referralAmountFormated = currency(referralAmount.value, 0);
	switch (userStore.invitationTestGroup) {
		case 'venture':
			return `Earn ${referralAmountFormated} in venture capital shares`;
		case 'flagship':
			return `Earn ${referralAmountFormated} in shares`;
		case 'control':
		default:
			return `Earn a ${referralAmountFormated} voucher`;
	}
});

const profileAndPreferencesLink = computed((): string => {
	return accountSetupChecklistStore.allTasks.length
		? 'profile-account-setup-checklist'
		: 'profile-contact-information-view';
});

function toggleDropdown(): void {
	emit('expandUserDropdown', !props.expanded);
}

function onClickAway(): void {
	emit('expandUserDropdown', false);
}
</script>

<style lang="scss" scoped>
@use '../../../styles/constants/colors' as *;

li.bg-color-gold-20 {
	border-bottom: 1px solid gold(90);
}

.text-transform-none {
	text-transform: none;
}
</style>
